import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, Settings, Users, Image as Img, FileText, LayoutTemplate, Wrench, ListChecks, ChartBar, FolderCog, FileSpreadsheet, PanelsTopLeft, Megaphone, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { purgeEmailTechnicalData } from '../../services/adminEmails';

const AdminSystem = () => {
  const { token } = useAuth();
  const [purging, setPurging] = useState(false);

  const sections = [
    {
      title: 'Surveillance & Journaux',
      items: [
        { label: 'Journaux connexions/inscriptions', to: '/admin/audit/users', icon: ChartBar },
        { label: 'Journaux système', to: '/admin/audit-logs', icon: FileSpreadsheet },
      ]
    },
    {
      title: 'Paramétrage du site',
      items: [
        { label: 'Mode maintenance & messages', to: '/admin/site-settings', icon: Settings },
        { label: 'Navigation du site', to: '/admin/navigation', icon: ListChecks },
        { label: 'Annonces (bientôt)', to: '/admin/announcements', icon: Megaphone, disabled: true },
      ]
    },
    {
      title: 'Contenus & Médias',
      items: [
        { label: "Gérer l'équipe", to: '/admin/team', icon: Users },
        { label: 'Media library', to: '/admin/media', icon: Img },
        { label: 'Page Builder', to: '/admin/page-builder', icon: LayoutTemplate },
        { label: 'Éditeur de contenu', to: '/admin/editor', icon: FileText },
      ]
    },
    {
      title: 'Paiements & Boutique',
      items: [
        { label: 'Paramètres de paiement', to: '/admin/payment-settings', icon: FolderCog },
        { label: 'Boutique (bientôt)', to: '/admin/shop', icon: PanelsTopLeft, disabled: true },
      ]
    },
    {
      title: 'Outils & Assistance',
      items: [
        { label: 'Assistant de configuration', to: '/admin/setup', icon: Wrench },
      ]
    },
  ];

  const handlePurge = async () => {
    if (!token) { window.alert('Session expirée. Merci de vous reconnecter.'); return; }
    const ok = window.confirm("Cette action supprime uniquement les jetons techniques (reset/vérification) des non‑fondateurs. Les comptes et emails ne sont pas modifiés. Continuer ?");
    if (!ok) return;
    setPurging(true);
    try {
      const res = await purgeEmailTechnicalData({ token, include_verification: true, include_reset: true });
      window.alert(`Purge effectuée:\nUtilisateurs concernés: ${res.emails_count}\nTokens reset supprimés: ${res.reset_tokens_deleted}\nTokens vérif supprimés: ${res.verification_tokens_deleted}`);
    } catch (e) {
      window.alert(`Erreur lors de la purge: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="h-5 w-5"/> Administration système</h1>
          <p className="text-sm text-muted-foreground">Retrouvez ici tous les outils d'administration du site PADAID.</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline"><Link to="/admin/site-settings">Mode maintenance</Link></Button>
          <Button variant="destructive" onClick={handlePurge} disabled={purging} className="inline-flex items-center gap-2">
            <Trash2 className="h-4 w-4"/>
            {purging ? 'Purge en cours…' : 'Purger données email (non‑fondateurs)'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sections.map((sec) => (
          <Card key={sec.title} className="border-border/50">
            <CardHeader>
              <CardTitle>{sec.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sec.items.map((it) => {
                  const Icon = it.icon;
                  const content = (
                    <div className={`w-full h-full flex items-center justify-between p-4 rounded-lg border ${it.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/40'} transition-colors`}>
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-primary"/>
                        <div>
                          <div className="font-medium text-sm">{it.label}</div>
                          {it.disabled && <div className="text-[11px] text-muted-foreground">Bientôt disponible</div>}
                        </div>
                      </div>
                      {!it.disabled && <span className="text-xs text-muted-foreground">Ouvrir →</span>}
                    </div>
                  );
                  return it.disabled ? (
                    <div key={it.label}>{content}</div>
                  ) : (
                    <Link key={it.label} to={it.to}>{content}</Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminSystem;